/**
 * Open.video 30-Day Launch Calendar
 *
 * The guide document is unchanged; only its wrapper is. It used to carry
 * its own 49px strip with a back link and no footer, so a visitor who landed
 * here from an email had no wordmark, no account control and no way into the
 * rest of the site. GuideShell supplies all three, and GuideFrame lets the
 * document size itself so the footer sits after it rather than behind a
 * second scrollbar.
 */

import GuideShell from "@/components/GuideShell";
import GuideFrame from "@/components/GuideFrame";

const HTML_SRC = `<!DOCTYPE html>
<html lang="en">
<head>
<meta charset="UTF-8">
<meta name="viewport" content="width=device-width, initial-scale=1.0">
<title>Open.video 30-Day Launch Calendar | Caren Glasser</title>
<link rel="preconnect" href="https://fonts.googleapis.com">
<link rel="preconnect" href="https://fonts.gstatic.com" crossorigin>
<link href="https://fonts.googleapis.com/css2?family=Fraunces:ital,opsz,wght@0,9..144,400;0,9..144,500;0,9..144,600;0,9..144,700;1,9..144,400;1,9..144,600&family=Plus+Jakarta+Sans:wght@400;500;600;700&display=swap" rel="stylesheet">

<style>
  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

  :root {
    --cream: oklch(0.98 0.008 85);
    --linen: oklch(0.93 0.01 85);
    --espresso: oklch(0.22 0.015 65);
    --terracotta: oklch(0.32 0.08 255);
    --border: oklch(0.88 0.012 85);
    --white: oklch(1 0.004 85);
    --terracotta-light: oklch(0.32 0.08 255 / 0.08);
    --terracotta-mid: oklch(0.32 0.08 255 / 0.22);
    --muted: oklch(0.52 0.015 65);
    --body: oklch(0.52 0.015 65);
  }

  body {
    background: var(--cream);
    color: var(--espresso);
    font-family: 'Plus Jakarta Sans', system-ui, sans-serif;
    min-height: 100vh;
    padding: 40px 20px 60px;
  }

  .container {
    max-width: 680px;
    margin: 0 auto;
  }

  /* HEADER */
  .header {
    text-align: center;
    margin-bottom: 40px;
    padding-bottom: 36px;
    border-bottom: 1px solid var(--border);
  }

  .header .eyebrow {
    font-family: 'Plus Jakarta Sans', system-ui, sans-serif;
    font-size: 12px;
    font-weight: 600;
    letter-spacing: 0.14em;
    text-transform: uppercase;
    color: var(--terracotta);
    margin-bottom: 14px;
  }

  .header h1 {
    font-family: 'Fraunces', Georgia, serif;
    font-size: clamp(26px, 5vw, 36px);
    font-weight: 700;
    color: var(--espresso);
    line-height: 1.2;
    margin-bottom: 14px;
  }

  .header p {
    font-size: 16px;
    color: var(--muted);
    line-height: 1.6;
    max-width: 460px;
    margin: 0 auto 24px;
  }

  /* HEADER ACTIONS */
  .header-actions {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 12px;
    flex-wrap: wrap;
  }

  .action-btn {
    font-family: 'Plus Jakarta Sans', system-ui, sans-serif;
    font-size: 12px;
    font-weight: 600;
    letter-spacing: 0.06em;
    padding: 9px 20px;
    border-radius: 2px;
    cursor: pointer;
    transition: all 0.2s ease;
    border: 1.5px solid var(--border);
    background: var(--white);
    color: var(--muted);
  }

  .action-btn:hover {
    border-color: var(--terracotta-mid);
    color: var(--espresso);
    background: var(--terracotta-light);
  }

  .action-btn.primary {
    background: var(--terracotta);
    border-color: var(--terracotta);
    color: var(--white);
  }

  .action-btn.primary:hover { background: oklch(0.26 0.075 255); border-color: oklch(0.26 0.075 255); }

  /* PROGRESS STRIP */
  .progress-strip {
    display: flex;
    gap: 4px;
    margin-bottom: 32px;
    flex-wrap: wrap;
  }

  .day-dot {
    width: 18px;
    height: 18px;
    border-radius: 50%;
    border: 1.5px solid var(--border);
    background: var(--white);
    cursor: pointer;
    transition: all 0.2s ease;
    flex-shrink: 0;
  }

  .day-dot:hover { border-color: var(--terracotta-mid); background: var(--terracotta-light); }
  .day-dot.done { background: var(--terracotta); border-color: var(--terracotta); }
  .day-dot.rest { background: var(--linen); border-color: var(--border); cursor: default; }

  .progress-meta {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 10px;
  }

  .progress-label {
    font-family: 'Plus Jakarta Sans', system-ui, sans-serif;
    font-size: 12px;
    font-weight: 600;
    letter-spacing: 0.1em;
    text-transform: uppercase;
    color: var(--muted);
  }

  .progress-count {
    font-family: 'Plus Jakarta Sans', system-ui, sans-serif;
    font-size: 12px;
    color: var(--muted);
  }

  .progress-count span {
    color: var(--terracotta);
    font-weight: 600;
  }

  /* WEEK BLOCKS */
  .week {
    margin-bottom: 16px;
    border: 1px solid var(--border);
    border-radius: 3px;
    background: var(--white);
    overflow: hidden;
    transition: all 0.3s ease;
  }

  .week.completed { border-color: var(--terracotta-mid); }

  .week-header {
    display: flex;
    align-items: center;
    gap: 16px;
    padding: 18px 24px;
    cursor: pointer;
    user-select: none;
    transition: background 0.2s ease;
  }

  .week-header:hover { background: var(--terracotta-light); }

  .week-check {
    width: 26px;
    height: 26px;
    border: 2px solid var(--border);
    border-radius: 50%;
    flex-shrink: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.3s ease;
    background: var(--white);
  }

  .week.completed .week-check {
    background: var(--terracotta);
    border-color: var(--terracotta);
  }

  .week-check svg {
    opacity: 0;
    transform: scale(0.4);
    transition: all 0.25s ease;
  }

  .week.completed .week-check svg { opacity: 1; transform: scale(1); }

  .week-title-wrap { flex: 1; }

  .week-label {
    font-family: 'Plus Jakarta Sans', system-ui, sans-serif;
    font-size: 12px;
    font-weight: 600;
    letter-spacing: 0.12em;
    text-transform: uppercase;
    color: var(--terracotta);
    margin-bottom: 3px;
  }

  .week.completed .week-label { color: oklch(0.26 0.075 255); }

  .week-theme {
    font-family: 'Fraunces', Georgia, serif;
    font-size: 18px;
    font-weight: 600;
    color: var(--espresso);
    line-height: 1.2;
  }

  .week-days-preview {
    font-family: 'Plus Jakarta Sans', system-ui, sans-serif;
    font-size: 12px;
    color: var(--muted);
    margin-top: 2px;
  }

  .week-toggle {
    font-family: 'Plus Jakarta Sans', system-ui, sans-serif;
    font-size: 20px;
    color: var(--border);
    flex-shrink: 0;
    transition: transform 0.3s ease, color 0.3s ease;
    line-height: 1;
  }

  .week.open .week-toggle { transform: rotate(45deg); color: var(--terracotta); }

  /* WEEK BODY */
  .week-body {
    display: none;
    border-top: 1px solid var(--border);
  }

  .week.open .week-body { display: block; }

  /* DAY ROWS */
  .day-row {
    display: flex;
    align-items: flex-start;
    gap: 0;
    border-bottom: 1px solid var(--border);
    transition: background 0.15s ease;
    cursor: pointer;
  }

  .day-row:last-child { border-bottom: none; }
  .day-row:hover { background: var(--terracotta-light); }

  .day-row.done-row { background: var(--linen); }
  .day-row.done-row:hover { background: oklch(0.93 0.01 85); }

  .day-row.rest-row {
    background: var(--cream);
    cursor: default;
  }

  .day-row.rest-row:hover { background: var(--cream); }

  .day-num {
    width: 56px;
    flex-shrink: 0;
    padding: 14px 0 14px 24px;
    font-family: 'Plus Jakarta Sans', system-ui, sans-serif;
    font-size: 12px;
    font-weight: 600;
    letter-spacing: 0.08em;
    color: var(--terracotta);
    text-transform: uppercase;
  }

  .day-row.rest-row .day-num { color: var(--muted); }
  .day-row.done-row .day-num { color: oklch(0.26 0.075 255); }

  .day-content {
    flex: 1;
    padding: 14px 16px 14px 0;
  }

  .day-task {
    font-size: 14px;
    line-height: 1.5;
    color: var(--body);
    transition: all 0.2s ease;
  }

  .day-row.done-row .day-task {
    text-decoration: line-through;
    color: var(--muted);
  }

  .day-row.rest-row .day-task {
    color: var(--muted);
    font-style: italic;
  }

  .day-check-col {
    width: 52px;
    flex-shrink: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 14px 16px 14px 0;
  }

  .day-checkbox {
    width: 18px;
    height: 18px;
    border: 1.5px solid var(--border);
    border-radius: 2px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: var(--white);
    transition: all 0.2s ease;
    flex-shrink: 0;
  }

  .day-row.done-row .day-checkbox {
    background: var(--terracotta);
    border-color: var(--terracotta);
  }

  .day-checkbox svg {
    opacity: 0;
    transform: scale(0.4);
    transition: all 0.2s ease;
  }

  .day-row.done-row .day-checkbox svg { opacity: 1; transform: scale(1); }

  /* WEEK FOOTER */
  .week-footer {
    padding: 14px 24px;
    border-top: 1px solid var(--border);
    display: flex;
    align-items: center;
    justify-content: space-between;
    background: var(--linen);
  }

  .week-progress-text {
    font-family: 'Plus Jakarta Sans', system-ui, sans-serif;
    font-size: 12px;
    color: var(--muted);
  }

  .week-progress-text span { color: var(--terracotta); font-weight: 600; }

  .mark-week-btn {
    font-family: 'Plus Jakarta Sans', system-ui, sans-serif;
    font-size: 12px;
    font-weight: 600;
    letter-spacing: 0.04em;
    padding: 8px 18px;
    background: var(--terracotta);
    color: var(--white);
    border: none;
    border-radius: 2px;
    cursor: pointer;
    transition: background 0.2s ease;
  }

  .mark-week-btn:hover { background: oklch(0.26 0.075 255); }
  .week.completed .mark-week-btn { background: oklch(0.26 0.075 255); }

  /* COMPLETION */
  .completion {
    display: none;
    text-align: center;
    padding: 40px 28px;
    background: var(--white);
    border: 1px solid var(--terracotta-mid);
    border-radius: 3px;
    margin-top: 8px;
  }

  .completion.visible { display: block; }
  .completion .icon { font-size: 36px; margin-bottom: 16px; }

  .completion h2 {
    font-family: 'Fraunces', Georgia, serif;
    font-size: 24px;
    color: var(--espresso);
    margin-bottom: 10px;
  }

  .completion p {
    font-size: 16px;
    color: var(--muted);
    line-height: 1.6;
    max-width: 420px;
    margin: 0 auto 20px;
  }

  .completion a {
    display: inline-block;
    padding: 12px 28px;
    background: var(--terracotta);
    color: var(--white);
    font-family: 'Plus Jakarta Sans', system-ui, sans-serif;
    font-size: 14px;
    font-weight: 600;
    border-radius: 2px;
    text-decoration: none;
    transition: background 0.2s;
  }

  .completion a:hover { background: oklch(0.26 0.075 255); }

  /* FOOTER */
  .footer {
    text-align: center;
    margin-top: 48px;
    padding-top: 28px;
    border-top: 1px solid var(--border);
  }

  .footer p {
    font-family: 'Plus Jakarta Sans', system-ui, sans-serif;
    font-size: 12px;
    color: oklch(0.88 0.012 85);
    letter-spacing: 0.04em;
  }

  .footer a { color: var(--terracotta); text-decoration: none; }

  /* PRINT STYLES */
  @media print {
    body { padding: 20px; background: white; }
    .header-actions { display: none; }
    .week { break-inside: avoid; border: 1px solid #ccc; }
    .week-body { display: block !important; }
    .week-toggle { display: none; }
    .completion { display: none !important; }
    .footer { margin-top: 20px; }
    .progress-strip { display: none; }
    .progress-meta { display: none; }
    .week-footer { display: none; }
    .day-check-col { display: none; }
    .day-row:hover { background: none !important; }
  }
</style>
</head>
<body>
<div class="container">

  <!-- HEADER -->
  <div class="header">
    <p class="eyebrow">Resource Center: Action Plan</p>
    <h1>Open.video<br>30-Day Launch Calendar</h1>
    <p>Your day-by-day roadmap from setup to scale. Check off days as you go, or print it and post it where you'll see it.</p>
    <div class="header-actions">
      <button class="action-btn" onclick="expandAll()">Expand all weeks</button>
      <button class="action-btn" onclick="collapseAll()">Collapse all</button>
      <button class="action-btn primary" onclick="window.print()">Print calendar</button>
    </div>
  </div>

  <!-- PROGRESS STRIP -->
  <div class="progress-meta">
    <span class="progress-label">Your progress</span>
    <span class="progress-count"><span id="doneCount">0</span> of 26 days complete</span>
  </div>
  <div class="progress-strip" id="progressStrip"></div>

  <!-- WEEK 1 -->
  <div class="week open" id="week1">
    <div class="week-header" onclick="toggleWeek('week1')">
      <div class="week-check">
        <svg width="12" height="12" viewBox="0 0 12 12" fill="none"><path d="M2 6L5 9L10 3" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>
      </div>
      <div class="week-title-wrap">
        <div class="week-label">Week 1 · Days 1–7</div>
        <div class="week-theme">Foundation</div>
        <div class="week-days-preview">Channel setup, first uploads, first embed</div>
      </div>
      <div class="week-toggle">+</div>
    </div>
    <div class="week-body">
      <div class="day-row" onclick="toggleDay(this, 1)" data-day="1">
        <div class="day-num">Day 1</div>
        <div class="day-content"><div class="day-task">Create channel, upload logo, set brand colors</div></div>
        <div class="day-check-col"><div class="day-checkbox"><svg width="10" height="10" viewBox="0 0 10 10" fill="none"><path d="M1.5 5L4 7.5L8.5 2.5" stroke="white" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg></div></div>
      </div>
      <div class="day-row" onclick="toggleDay(this, 2)" data-day="2">
        <div class="day-num">Day 2</div>
        <div class="day-content"><div class="day-task">Upload 3 starter videos</div></div>
        <div class="day-check-col"><div class="day-checkbox"><svg width="10" height="10" viewBox="0 0 10 10" fill="none"><path d="M1.5 5L4 7.5L8.5 2.5" stroke="white" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg></div></div>
      </div>
      <div class="day-row" onclick="toggleDay(this, 3)" data-day="3">
        <div class="day-num">Day 3</div>
        <div class="day-content"><div class="day-task">Optimize metadata: write SEO titles and descriptions for each video</div></div>
        <div class="day-check-col"><div class="day-checkbox"><svg width="10" height="10" viewBox="0 0 10 10" fill="none"><path d="M1.5 5L4 7.5L8.5 2.5" stroke="white" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg></div></div>
      </div>
      <div class="day-row" onclick="toggleDay(this, 4)" data-day="4">
        <div class="day-num">Day 4</div>
        <div class="day-content"><div class="day-task">Create your first playlist</div></div>
        <div class="day-check-col"><div class="day-checkbox"><svg width="10" height="10" viewBox="0 0 10 10" fill="none"><path d="M1.5 5L4 7.5L8.5 2.5" stroke="white" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg></div></div>
      </div>
      <div class="day-row" onclick="toggleDay(this, 5)" data-day="5">
        <div class="day-num">Day 5</div>
        <div class="day-content"><div class="day-task">Embed 1 video on your website or blog</div></div>
        <div class="day-check-col"><div class="day-checkbox"><svg width="10" height="10" viewBox="0 0 10 10" fill="none"><path d="M1.5 5L4 7.5L8.5 2.5" stroke="white" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg></div></div>
      </div>
      <div class="day-row" onclick="toggleDay(this, 6)" data-day="6">
        <div class="day-num">Day 6</div>
        <div class="day-content"><div class="day-task">Share your channel link on LinkedIn</div></div>
        <div class="day-check-col"><div class="day-checkbox"><svg width="10" height="10" viewBox="0 0 10 10" fill="none"><path d="M1.5 5L4 7.5L8.5 2.5" stroke="white" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg></div></div>
      </div>
      <div class="day-row rest-row" data-day="7">
        <div class="day-num">Day 7</div>
        <div class="day-content"><div class="day-task">Rest &amp; recap: review what you built this week</div></div>
        <div class="day-check-col"></div>
      </div>
    </div>
    <div class="week-footer">
      <span class="week-progress-text"><span id="wk1count">0</span> of 6 days done</span>
      <button class="mark-week-btn" onclick="markWeek('week1', [1,2,3,4,5,6])">✓ Mark week complete</button>
    </div>
  </div>

  <!-- WEEK 2 -->
  <div class="week" id="week2">
    <div class="week-header" onclick="toggleWeek('week2')">
      <div class="week-check">
        <svg width="12" height="12" viewBox="0 0 12 12" fill="none"><path d="M2 6L5 9L10 3" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>
      </div>
      <div class="week-title-wrap">
        <div class="week-label">Week 2 · Days 8–14</div>
        <div class="week-theme">Content Expansion</div>
        <div class="week-days-preview">New videos, CTAs, email integration, first Substack embed</div>
      </div>
      <div class="week-toggle">+</div>
    </div>
    <div class="week-body">
      <div class="day-row" onclick="toggleDay(this, 8)" data-day="8">
        <div class="day-num">Day 8</div>
        <div class="day-content"><div class="day-task">Upload 2 new videos</div></div>
        <div class="day-check-col"><div class="day-checkbox"><svg width="10" height="10" viewBox="0 0 10 10" fill="none"><path d="M1.5 5L4 7.5L8.5 2.5" stroke="white" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg></div></div>
      </div>
      <div class="day-row" onclick="toggleDay(this, 9)" data-day="9">
        <div class="day-num">Day 9</div>
        <div class="day-content"><div class="day-task">Add SEO keywords and tags to all new videos</div></div>
        <div class="day-check-col"><div class="day-checkbox"><svg width="10" height="10" viewBox="0 0 10 10" fill="none"><path d="M1.5 5L4 7.5L8.5 2.5" stroke="white" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg></div></div>
      </div>
      <div class="day-row" onclick="toggleDay(this, 10)" data-day="10">
        <div class="day-num">Day 10</div>
        <div class="day-content"><div class="day-task">Create playlist #2</div></div>
        <div class="day-check-col"><div class="day-checkbox"><svg width="10" height="10" viewBox="0 0 10 10" fill="none"><path d="M1.5 5L4 7.5L8.5 2.5" stroke="white" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg></div></div>
      </div>
      <div class="day-row" onclick="toggleDay(this, 11)" data-day="11">
        <div class="day-num">Day 11</div>
        <div class="day-content"><div class="day-task">Add a CTA overlay to 1 video</div></div>
        <div class="day-check-col"><div class="day-checkbox"><svg width="10" height="10" viewBox="0 0 10 10" fill="none"><path d="M1.5 5L4 7.5L8.5 2.5" stroke="white" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg></div></div>
      </div>
      <div class="day-row" onclick="toggleDay(this, 12)" data-day="12">
        <div class="day-num">Day 12</div>
        <div class="day-content"><div class="day-task">Integrate email opt-in form (Mailchimp, ConvertKit, or Substack)</div></div>
        <div class="day-check-col"><div class="day-checkbox"><svg width="10" height="10" viewBox="0 0 10 10" fill="none"><path d="M1.5 5L4 7.5L8.5 2.5" stroke="white" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg></div></div>
      </div>
      <div class="day-row" onclick="toggleDay(this, 13)" data-day="13">
        <div class="day-num">Day 13</div>
        <div class="day-content"><div class="day-task">Create a teaser clip for TikTok or Reels linking back to your channel</div></div>
        <div class="day-check-col"><div class="day-checkbox"><svg width="10" height="10" viewBox="0 0 10 10" fill="none"><path d="M1.5 5L4 7.5L8.5 2.5" stroke="white" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg></div></div>
      </div>
      <div class="day-row" onclick="toggleDay(this, 14)" data-day="14">
        <div class="day-num">Day 14</div>
        <div class="day-content"><div class="day-task">Publish a Substack post with your first embedded video</div></div>
        <div class="day-check-col"><div class="day-checkbox"><svg width="10" height="10" viewBox="0 0 10 10" fill="none"><path d="M1.5 5L4 7.5L8.5 2.5" stroke="white" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg></div></div>
      </div>
    </div>
    <div class="week-footer">
      <span class="week-progress-text"><span id="wk2count">0</span> of 7 days done</span>
      <button class="mark-week-btn" onclick="markWeek('week2', [8,9,10,11,12,13,14])">✓ Mark week complete</button>
    </div>
  </div>

  <!-- WEEK 3 -->
  <div class="week" id="week3">
    <div class="week-header" onclick="toggleWeek('week3')">
      <div class="week-check">
        <svg width="12" height="12" viewBox="0 0 12 12" fill="none"><path d="M2 6L5 9L10 3" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>
      </div>
      <div class="week-title-wrap">
        <div class="week-label">Week 3 · Days 15–21</div>
        <div class="week-theme">Audience Growth</div>
        <div class="week-days-preview">Cross-platform promotion, email list, YouTube teaser</div>
      </div>
      <div class="week-toggle">+</div>
    </div>
    <div class="week-body">
      <div class="day-row" onclick="toggleDay(this, 15)" data-day="15">
        <div class="day-num">Day 15</div>
        <div class="day-content"><div class="day-task">Upload 2 more videos</div></div>
        <div class="day-check-col"><div class="day-checkbox"><svg width="10" height="10" viewBox="0 0 10 10" fill="none"><path d="M1.5 5L4 7.5L8.5 2.5" stroke="white" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg></div></div>
      </div>
      <div class="day-row" onclick="toggleDay(this, 16)" data-day="16">
        <div class="day-num">Day 16</div>
        <div class="day-content"><div class="day-task">Optimize video descriptions with external links</div></div>
        <div class="day-check-col"><div class="day-checkbox"><svg width="10" height="10" viewBox="0 0 10 10" fill="none"><path d="M1.5 5L4 7.5L8.5 2.5" stroke="white" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg></div></div>
      </div>
      <div class="day-row" onclick="toggleDay(this, 17)" data-day="17">
        <div class="day-num">Day 17</div>
        <div class="day-content"><div class="day-task">Promote your channel in a LinkedIn post</div></div>
        <div class="day-check-col"><div class="day-checkbox"><svg width="10" height="10" viewBox="0 0 10 10" fill="none"><path d="M1.5 5L4 7.5L8.5 2.5" stroke="white" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg></div></div>
      </div>
      <div class="day-row" onclick="toggleDay(this, 18)" data-day="18">
        <div class="day-num">Day 18</div>
        <div class="day-content"><div class="day-task">Create a YouTube teaser driving traffic back to your channel</div></div>
        <div class="day-check-col"><div class="day-checkbox"><svg width="10" height="10" viewBox="0 0 10 10" fill="none"><path d="M1.5 5L4 7.5L8.5 2.5" stroke="white" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg></div></div>
      </div>
      <div class="day-row" onclick="toggleDay(this, 19)" data-day="19">
        <div class="day-num">Day 19</div>
        <div class="day-content"><div class="day-task">Add another CTA overlay to a playlist video</div></div>
        <div class="day-check-col"><div class="day-checkbox"><svg width="10" height="10" viewBox="0 0 10 10" fill="none"><path d="M1.5 5L4 7.5L8.5 2.5" stroke="white" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg></div></div>
      </div>
      <div class="day-row" onclick="toggleDay(this, 20)" data-day="20">
        <div class="day-num">Day 20</div>
        <div class="day-content"><div class="day-task">Email your list: highlight your new channel</div></div>
        <div class="day-check-col"><div class="day-checkbox"><svg width="10" height="10" viewBox="0 0 10 10" fill="none"><path d="M1.5 5L4 7.5L8.5 2.5" stroke="white" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg></div></div>
      </div>
      <div class="day-row rest-row" data-day="21">
        <div class="day-num">Day 21</div>
        <div class="day-content"><div class="day-task">Rest &amp; recap: review growth so far</div></div>
        <div class="day-check-col"></div>
      </div>
    </div>
    <div class="week-footer">
      <span class="week-progress-text"><span id="wk3count">0</span> of 6 days done</span>
      <button class="mark-week-btn" onclick="markWeek('week3', [15,16,17,18,19,20])">✓ Mark week complete</button>
    </div>
  </div>

  <!-- WEEK 4 -->
  <div class="week" id="week4">
    <div class="week-header" onclick="toggleWeek('week4')">
      <div class="week-check">
        <svg width="12" height="12" viewBox="0 0 12 12" fill="none"><path d="M2 6L5 9L10 3" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>
      </div>
      <div class="week-title-wrap">
        <div class="week-label">Week 4 · Days 22–30</div>
        <div class="week-theme">Scaling</div>
        <div class="week-days-preview">Analytics review, live Q&amp;A, month 2 planning</div>
      </div>
      <div class="week-toggle">+</div>
    </div>
    <div class="week-body">
      <div class="day-row" onclick="toggleDay(this, 22)" data-day="22">
        <div class="day-num">Day 22</div>
        <div class="day-content"><div class="day-task">Upload 2 more videos</div></div>
        <div class="day-check-col"><div class="day-checkbox"><svg width="10" height="10" viewBox="0 0 10 10" fill="none"><path d="M1.5 5L4 7.5L8.5 2.5" stroke="white" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg></div></div>
      </div>
      <div class="day-row" onclick="toggleDay(this, 23)" data-day="23">
        <div class="day-num">Day 23</div>
        <div class="day-content"><div class="day-task">Build playlist #3</div></div>
        <div class="day-check-col"><div class="day-checkbox"><svg width="10" height="10" viewBox="0 0 10 10" fill="none"><path d="M1.5 5L4 7.5L8.5 2.5" stroke="white" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg></div></div>
      </div>
      <div class="day-row" onclick="toggleDay(this, 24)" data-day="24">
        <div class="day-num">Day 24</div>
        <div class="day-content"><div class="day-task">Share your channel across all social platforms</div></div>
        <div class="day-check-col"><div class="day-checkbox"><svg width="10" height="10" viewBox="0 0 10 10" fill="none"><path d="M1.5 5L4 7.5L8.5 2.5" stroke="white" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg></div></div>
      </div>
      <div class="day-row" onclick="toggleDay(this, 25)" data-day="25">
        <div class="day-num">Day 25</div>
        <div class="day-content"><div class="day-task">Review analytics: identify your top performing video</div></div>
        <div class="day-check-col"><div class="day-checkbox"><svg width="10" height="10" viewBox="0 0 10 10" fill="none"><path d="M1.5 5L4 7.5L8.5 2.5" stroke="white" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg></div></div>
      </div>
      <div class="day-row" onclick="toggleDay(this, 26)" data-day="26">
        <div class="day-num">Day 26</div>
        <div class="day-content"><div class="day-task">Double down: create a related video on your top topic</div></div>
        <div class="day-check-col"><div class="day-checkbox"><svg width="10" height="10" viewBox="0 0 10 10" fill="none"><path d="M1.5 5L4 7.5L8.5 2.5" stroke="white" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg></div></div>
      </div>
      <div class="day-row" onclick="toggleDay(this, 27)" data-day="27">
        <div class="day-num">Day 27</div>
        <div class="day-content"><div class="day-task">Post a Substack case study: "What I learned from my first month"</div></div>
        <div class="day-check-col"><div class="day-checkbox"><svg width="10" height="10" viewBox="0 0 10 10" fill="none"><path d="M1.5 5L4 7.5L8.5 2.5" stroke="white" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg></div></div>
      </div>
      <div class="day-row" onclick="toggleDay(this, 28)" data-day="28">
        <div class="day-num">Day 28</div>
        <div class="day-content"><div class="day-task">Run a live Q&amp;A driving traffic back to your channel</div></div>
        <div class="day-check-col"><div class="day-checkbox"><svg width="10" height="10" viewBox="0 0 10 10" fill="none"><path d="M1.5 5L4 7.5L8.5 2.5" stroke="white" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg></div></div>
      </div>
      <div class="day-row" onclick="toggleDay(this, 29)" data-day="29">
        <div class="day-num">Day 29</div>
        <div class="day-content"><div class="day-task">Prepare your month 2 content calendar</div></div>
        <div class="day-check-col"><div class="day-checkbox"><svg width="10" height="10" viewBox="0 0 10 10" fill="none"><path d="M1.5 5L4 7.5L8.5 2.5" stroke="white" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg></div></div>
      </div>
      <div class="day-row" onclick="toggleDay(this, 30)" data-day="30">
        <div class="day-num">Day 30</div>
        <div class="day-content"><div class="day-task">Post your launch recap: "My channel is live. Here's what I built."</div></div>
        <div class="day-check-col"><div class="day-checkbox"><svg width="10" height="10" viewBox="0 0 10 10" fill="none"><path d="M1.5 5L4 7.5L8.5 2.5" stroke="white" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg></div></div>
      </div>
    </div>
    <div class="week-footer">
      <span class="week-progress-text"><span id="wk4count">0</span> of 9 days done</span>
      <button class="mark-week-btn" onclick="markWeek('week4', [22,23,24,25,26,27,28,29,30])">✓ Mark week complete</button>
    </div>
  </div>

  <!-- COMPLETION -->
  <div class="completion" id="completion">
    <div class="icon">🎉</div>
    <h2>30 days. Done.</h2>
    <p>You built it, optimized it, distributed it, and kept going. That's 30 days most people never finish. Now start month 2.</p>
    <a href="https://aitools.yoursmartstack.com/" target="_blank">Explore the Resource Center →</a>
  </div>

  <!-- FOOTER -->

</div>

<script>
  const completedDays = new Set();
  const restDays = new Set([7, 21]);
  const totalDays = 26;

  const weekDays = {
    week1: [1,2,3,4,5,6],
    week2: [8,9,10,11,12,13,14],
    week3: [15,16,17,18,19,20],
    week4: [22,23,24,25,26,27,28,29,30]
  };

  const weekCounts = { week1: 6, week2: 7, week3: 6, week4: 9 };

  function buildStrip() {
    const strip = document.getElementById('progressStrip');
    strip.innerHTML = '';
    for (let d = 1; d <= 30; d++) {
      const dot = document.createElement('div');
      dot.className = 'day-dot';
      dot.id = 'dot' + d;
      dot.title = 'Day ' + d;
      if (restDays.has(d)) {
        dot.classList.add('rest');
      } else {
        dot.onclick = () => {
          const row = document.querySelector('[data-day="' + d + '"]');
          if (row && !row.classList.contains('rest-row')) toggleDay(row, d);
        };
      }
      strip.appendChild(dot);
    }
  }

  function toggleDay(row, dayNum) {
    if (row.classList.contains('rest-row')) return;
    const isDone = row.classList.contains('done-row');
    if (isDone) {
      row.classList.remove('done-row');
      completedDays.delete(dayNum);
    } else {
      row.classList.add('done-row');
      completedDays.add(dayNum);
    }
    updateDot(dayNum, !isDone);
    updateProgress();
    updateWeekCounters();
  }

  function updateDot(day, done) {
    const dot = document.getElementById('dot' + day);
    if (dot) dot.classList.toggle('done', done);
  }

  function markWeek(weekId, days) {
    const week = document.getElementById(weekId);
    days.forEach(d => {
      const row = document.querySelector('[data-day="' + d + '"]');
      if (row && !row.classList.contains('done-row')) {
        row.classList.add('done-row');
        completedDays.add(d);
        updateDot(d, true);
      }
    });
    week.classList.add('completed');
    week.classList.remove('open');
    updateProgress();
    updateWeekCounters();
  }

  function updateWeekCounters() {
    Object.keys(weekDays).forEach(wk => {
      const days = weekDays[wk];
      const done = days.filter(d => completedDays.has(d)).length;
      const num = wk.replace('week','');
      document.getElementById('wk' + num + 'count').textContent = done;
      if (done === weekCounts[wk]) {
        document.getElementById(wk).classList.add('completed');
      } else {
        document.getElementById(wk).classList.remove('completed');
      }
    });
  }

  function updateProgress() {
    const done = completedDays.size;
    document.getElementById('doneCount').textContent = done;
    if (done >= totalDays) {
      document.getElementById('completion').classList.add('visible');
    }
  }

  function toggleWeek(id) {
    const week = document.getElementById(id);
    const isOpen = week.classList.contains('open');
    document.querySelectorAll('.week').forEach(w => w.classList.remove('open'));
    if (!isOpen) week.classList.add('open');
  }

  function expandAll() {
    document.querySelectorAll('.week').forEach(w => w.classList.add('open'));
  }

  function collapseAll() {
    document.querySelectorAll('.week').forEach(w => w.classList.remove('open'));
  }

  buildStrip();
<\/script>
</body>
</html>`;

export default function OpenVideo30DayCalendar() {
  return (
    <GuideShell
      context={"Open.Video guide"}
      backHref={"/resources/open-video"}
      backLabel={"Back to Open.Video"}
    >
      <GuideFrame html={HTML_SRC} title={"Open.video 30-Day Launch Calendar"} />
    </GuideShell>
  );
}
